import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { BrandProvider } from "./context/BrandContext";
import PasswordGate from "./components/PasswordGate";
import NotFound from "./pages/NotFound";
import Home from "./pages/Home";
import CostcoLanding from "./pages/CostcoLanding";
import CostcoTerms from "./pages/CostcoTerms";
import ProcessPage from "./pages/ProcessPage";
import AwardsPage from "./pages/AwardsPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import ComplaintsPolicy from "./pages/ComplaintsPolicy";
import AboutUs from "./pages/AboutUs";
import Careers from "./pages/Careers";
import ContactUs from "./pages/ContactUs";
import Support from "./pages/Support";
import Calculators from "./pages/Calculators";
import ComingSoon from "./pages/ComingSoon";
import Blog from "./pages/Blog";
import FirstHomeBuyerGuide from "./pages/blog/FirstHomeBuyerGuide";
import WhenToRefinance from "./pages/blog/WhenToRefinance";
import FixedVsVariable from "./pages/blog/FixedVsVariable";
import BorrowingPower from "./pages/blog/BorrowingPower";
import BrokerVsBank from "./pages/blog/BrokerVsBank";
import CostcoHomeLoansGuide from "./pages/blog/CostcoHomeLoansGuide";
import Technology from "./pages/Technology";
import Changelog from "./pages/Changelog";

// Loan type pages
import FirstHomeBuyers from "./pages/loans/FirstHomeBuyers";
import NextHome from "./pages/loans/NextHome";
import Refinance from "./pages/loans/Refinance";
import InvestmentProperty from "./pages/loans/InvestmentProperty";
import PersonalLoansPage from "./pages/loans/PersonalLoans";
import CarLoans from "./pages/loans/CarLoans";
import CommercialLoansPage from "./pages/loans/CommercialLoans";
import AssetFinance from "./pages/loans/AssetFinance";

// Costco location pages
import CostcoAuburn from "./pages/costco-locations/Auburn";
import CostcoCasula from "./pages/costco-locations/Casula";
import CostcoMarsdenPark from "./pages/costco-locations/MarsdenPark";
import CostcoLakeMacquarie from "./pages/costco-locations/LakeMacquarie";
import CostcoArdeer from "./pages/costco-locations/Ardeer";
import CostcoEpping from "./pages/costco-locations/Epping";
import CostcoRingwood from "./pages/costco-locations/Ringwood";
import CostcoMoorabbin from "./pages/costco-locations/Moorabbin";
import CostcoNorthLakes from "./pages/costco-locations/NorthLakes";
import CostcoBundamba from "./pages/costco-locations/Bundamba";
import CostcoCoomera from "./pages/costco-locations/Coomera";
import CostcoPerth from "./pages/costco-locations/PerthAirport";
import CostcoCasuarina from "./pages/costco-locations/Casuarina";
import CostcoCanberra from "./pages/costco-locations/Canberra";
import CostcoAdelaide from "./pages/costco-locations/Kilburn";

// Location pages
import DarwinPage from "./pages/locations/Darwin";
import BrisbanePage from "./pages/locations/Brisbane";
import MelbournePage from "./pages/locations/Melbourne";
import SydneyPage from "./pages/locations/Sydney";
import PerthPage from "./pages/locations/Perth";
import AdelaidePage from "./pages/locations/Adelaide";
import HobartPage from "./pages/locations/Hobart";
import CanberraPage from "./pages/locations/Canberra";
import NewcastlePage from "./pages/locations/Newcastle";
import WollongongPage from "./pages/locations/Wollongong";
import GoldCoastPage from "./pages/locations/GoldCoast";
import SunshineCoastPage from "./pages/locations/SunshineCoast";
import GeelongPage from "./pages/locations/Geelong";
import TownsvillePage from "./pages/locations/Townsville";
import CairnsPage from "./pages/locations/Cairns";
import ToowoombaPage from "./pages/locations/Toowoomba";
import BallaratPage from "./pages/locations/Ballarat";
import BendigoPage from "./pages/locations/Bendigo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    // Small delay to let the page render before scrolling to the anchor
    const timer = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [pathname, hash]);

  return null;
}

function App() {
  useEffect(() => {
    const testConnection = async () => {
      try {
        const response = await axios.get(`${API}/`);
        console.log('Backend connected:', response.data.message);
      } catch (e) {
        console.error('Backend connection error:', e);
      }
    };
    testConnection();
  }, []);

  return (
    <HelmetProvider>
    <PasswordGate>
    <BrandProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToHash />
          <Routes>
            <Route path="/" element={<Home />} />

            {/* Loan type pages */}
            <Route path="/first-home-buyers" element={<FirstHomeBuyers />} />
            <Route path="/next-home" element={<NextHome />} />
            <Route path="/refinance" element={<Refinance />} />
            <Route path="/investment-property" element={<InvestmentProperty />} />
            <Route path="/personal-loans" element={<PersonalLoansPage />} />
            <Route path="/car-loans" element={<CarLoans />} />
            <Route path="/commercial-loans" element={<CommercialLoansPage />} />
            <Route path="/asset-finance" element={<AssetFinance />} />

            {/* Location pages */}
            <Route path="/locations/darwin" element={<DarwinPage />} />
            <Route path="/locations/brisbane" element={<BrisbanePage />} />
            <Route path="/locations/melbourne" element={<MelbournePage />} />
            <Route path="/locations/sydney" element={<SydneyPage />} />
            <Route path="/locations/perth" element={<PerthPage />} />
            <Route path="/locations/adelaide" element={<AdelaidePage />} />
            <Route path="/locations/hobart" element={<HobartPage />} />
            <Route path="/locations/canberra" element={<CanberraPage />} />
            <Route path="/locations/newcastle" element={<NewcastlePage />} />
            <Route path="/locations/wollongong" element={<WollongongPage />} />
            <Route path="/locations/gold-coast" element={<GoldCoastPage />} />
            <Route path="/locations/sunshine-coast" element={<SunshineCoastPage />} />
            <Route path="/locations/geelong" element={<GeelongPage />} />
            <Route path="/locations/townsville" element={<TownsvillePage />} />
            <Route path="/locations/cairns" element={<CairnsPage />} />
            <Route path="/locations/toowoomba" element={<ToowoombaPage />} />
            <Route path="/locations/ballarat" element={<BallaratPage />} />
            <Route path="/locations/bendigo" element={<BendigoPage />} />

            {/* Other pages */}
            <Route path="/process" element={<ProcessPage />} />
            <Route path="/awards" element={<AwardsPage />} />
            <Route path="/costco" element={<CostcoLanding />} />
            <Route path="/costco-terms" element={<CostcoTerms />} />

            {/* Costco location pages */}
            <Route path="/costco/auburn" element={<CostcoAuburn />} />
            <Route path="/costco/casula" element={<CostcoCasula />} />
            <Route path="/costco/marsden-park" element={<CostcoMarsdenPark />} />
            <Route path="/costco/lake-macquarie" element={<CostcoLakeMacquarie />} />
            <Route path="/costco/ardeer" element={<CostcoArdeer />} />
            <Route path="/costco/epping" element={<CostcoEpping />} />
            <Route path="/costco/ringwood" element={<CostcoRingwood />} />
            <Route path="/costco/moorabbin" element={<CostcoMoorabbin />} />
            <Route path="/costco/north-lakes" element={<CostcoNorthLakes />} />
            <Route path="/costco/ipswich" element={<CostcoBundamba />} />
            <Route path="/costco/gold-coast" element={<CostcoCoomera />} />
            <Route path="/costco/perth" element={<CostcoPerth />} />
            <Route path="/costco/casuarina" element={<CostcoCasuarina />} />
            <Route path="/costco/canberra" element={<CostcoCanberra />} />
            <Route path="/costco/adelaide" element={<CostcoAdelaide />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/complaints-policy" element={<ComplaintsPolicy />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/support" element={<Support />} />
            <Route path="/calculators" element={<Calculators />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/first-home-buyer-guide" element={<FirstHomeBuyerGuide />} />
            <Route path="/blog/when-to-refinance" element={<WhenToRefinance />} />
            <Route path="/blog/fixed-vs-variable" element={<FixedVsVariable />} />
            <Route path="/blog/borrowing-power" element={<BorrowingPower />} />
            <Route path="/blog/broker-vs-bank" element={<BrokerVsBank />} />
            <Route path="/blog/costco-home-loans-guide" element={<CostcoHomeLoansGuide />} />
            <Route path="/technology" element={<Technology />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/news" element={<ComingSoon title="News" description="The latest news and updates from Manage Your Loans — coming soon." path="/news" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </div>
    </BrandProvider>
    </PasswordGate>
    </HelmetProvider>
  );
}

export default App;